import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { logout } from '../../../actions/auth';
import { setMenuTab, setSidebarCollapsed } from '../../../actions/ui';
import { SidebarTab } from '../../../constants/paths/internal';
import { StoreState } from '../../../meta/types/store';
import { Claims, SentinelUser, DomainAccountWithDomain } from '../../../meta/types/user';
import Sidebar from './Sidebar';
import { NewsEntry } from '../../../meta/types/news';
import { fetchUserData, fetchNews, markNewsSeen } from '../../../actions/dashboard';
import { checkHasUnseenNews } from '../../../utils/dashboard';
import { UserData } from '../../../meta/types/userdata';

type SidebarContextProps = {
  sidebarCollapsed: boolean;
  activeTab: SidebarTab;
  authenticatedUser?: Claims;
  configurableUser?: SentinelUser;
  logout: any;
  rootManaging: boolean;
  hasUnseenNews: boolean;
  news?: NewsEntry[];
  newsError?: string;
  shAccount: DomainAccountWithDomain | undefined;
};

export const SidebarContext = React.createContext<SidebarContextProps>({
  sidebarCollapsed: false,
  activeTab: SidebarTab.DASHBOARD,
  authenticatedUser: undefined,
  configurableUser: undefined,
  logout: undefined,
  rootManaging: false,
  news: undefined,
  newsError: undefined,
  hasUnseenNews: false,
  shAccount: undefined,
});

interface SidebarProps {
  activeTab: SidebarTab;
  setMenuTab: (menuTab: SidebarTab) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  authenticatedUser?: Claims;
  configurableUser?: SentinelUser;
  rootManaging: boolean;
  logout: any;
  fetchNews: () => void;
  fetchUserData: () => void;
  markNewsSeen: () => void;
  news?: NewsEntry[];
  newsError?: string;
  userData?: UserData;
  shAccount: DomainAccountWithDomain | undefined;
}

const SidebarContainer = ({
  activeTab,
  sidebarCollapsed,
  setSidebarCollapsed,
  authenticatedUser,
  configurableUser,
  logout,
  rootManaging,
  fetchNews,
  fetchUserData,
  markNewsSeen,
  news,
  newsError,
  userData,
  shAccount,
}: SidebarProps) => {
  useEffect(() => {
    fetchNews();
    fetchUserData();
  }, [fetchNews, fetchUserData]);

  const [newsVisible, setNewsVisible] = useState(false);
  const hasUnseenNews = checkHasUnseenNews(news, userData);

  function toggleNews() {
    if (!newsVisible && hasUnseenNews) {
      markNewsSeen();
    }

    setNewsVisible(!newsVisible);
  }

  return (
    <SidebarContext.Provider
      value={{
        sidebarCollapsed,
        activeTab,
        authenticatedUser,
        configurableUser,
        logout,
        rootManaging,
        news,
        newsError,
        hasUnseenNews,
        shAccount,
      }}
    >
      <Sidebar
        setSidebarCollapsed={setSidebarCollapsed}
        newsVisible={newsVisible}
        toggleNews={toggleNews}
      />
    </SidebarContext.Provider>
  );
};

function mapStateToProps(state: StoreState) {
  return {
    authenticatedUser: state.auth.authenticatedUser,
    activeTab: state.ui.activeTab,
    sidebarCollapsed: state.ui.sidebarCollapsed,
    rootManaging: state.auth.rootManaging,
    news: state.dashboard.news,
    newsError: state.dashboard.newsError,
    userData: state.dashboard.userData,
    shAccount: state.account.accounts.find(
      acc => acc.domainId === state.auth.authenticatedUser?.did,
    ),
  };
}

const mapDispatchToProps = {
  setMenuTab,
  setSidebarCollapsed,
  logout,
  markNewsSeen,
  fetchNews,
  fetchUserData,
};

const ConnectedSidebarContainer = connect(mapStateToProps, mapDispatchToProps)(SidebarContainer);

export default ConnectedSidebarContainer;
